import './index.scss';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import { NavLink } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import { Skeleton } from '@material-ui/lab';
import { DeleteModal } from '@components/Modals';
import { translate } from '@utils';
import { isDeletingRoleLoading, roleIdForDelete, rolesSelector } from '@selectors/role';
import { makeRoles, makeDeleteRole, setRoleIdForDelete } from '@actions/roleActions';
import { setIsOpenRoleDeleteModal } from '@actions/rolesPageActions';
import { Paths } from '@constants/paths';
import { RolesPageAccordion } from './RolesPageAccordion';
import { pageNavigationRoutes } from '@constants/navigation';
import { FeaturePermission, PagePermission, PermissionActions } from '@enums';
import { hasPermissionFeature } from '@hooks/hasPermissionFeature';
import { makeAllSubmissionGroups } from '@actions/submissionGroupsActions';
import { rolesPageSelector } from '@selectors/rolesPage';
import { setUsers } from '@actions/userActions';
import { usersSelector } from '@selectors/user';
import { setUnits } from '@actions/unitActions';
import { unitsSelector } from '@selectors/unit';

const RolesPage = () => {
  const dispatch = useDispatch();

  const roles = useSelector(rolesSelector);
  const { isOpenRoleDeleteModal } = useSelector(rolesPageSelector);
  const isLoading = useSelector(isDeletingRoleLoading);
  const roleId = useSelector(roleIdForDelete);
  const usersWithPermissions = useSelector(usersSelector);
  const unitsWithPermissions = useSelector(unitsSelector);

  useEffect(() => {
    dispatch(makeRoles());
    dispatch(makeAllSubmissionGroups);
  }, []);

  const hasPermissionsForCreateRole = hasPermissionFeature(
    PagePermission.Roles,
    [FeaturePermission.RoleItem],
    [PermissionActions.Create]
  );

  const canViewRole = hasPermissionFeature(
    PagePermission.Roles,
    [FeaturePermission.RoleItem],
    [PermissionActions.View]
  );

  const handleCancelDeleteModal = () => {
    dispatch(setIsOpenRoleDeleteModal(false));
    dispatch(setRoleIdForDelete(null));
  };

  const handleConfirmationDeleteModal = () => {
    dispatch(makeDeleteRole(roleId));
    dispatch(setUsers(usersWithPermissions.filter((user) => user.roles[0].id !== roleId)));
    dispatch(setUnits(unitsWithPermissions.filter((unit) => unit.roles[0].id !== roleId)));
  };

  return (
    <div className="roles-container">
      <div className="roles-header-container">
        <Typography component="h3" variant="h6" className="roles-heading">
          {!roles.length ? <Skeleton /> : translate('roles')}
        </Typography>
        {hasPermissionsForCreateRole && canViewRole && (
          <NavLink
            to={`${Paths.DASHBOARD}${pageNavigationRoutes.ROLES}/create`}
            className="create-role-link-container"
          >
            <AddIcon fontSize="medium" />
            {translate('create_new_role')}
          </NavLink>
        )}
      </div>
      <RolesPageAccordion roles={roles} canViewRole={canViewRole} />
      <DeleteModal
        handleCancel={handleCancelDeleteModal}
        handleConfirmation={handleConfirmationDeleteModal}
        open={isOpenRoleDeleteModal}
        contentName="delete_role_modal_content"
        title={translate('delete_role_modal_title')}
        isDisabledConfirmationButton={isLoading}
      />
    </div>
  );
};

export { RolesPage };
